import { X, Zap, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import { useEffect, useState } from 'react';
import { supabase } from '../../utils/supabaseStorage';
import { SubscriptionPlanGrid, type SubscriptionPlanRow } from '../billing/SubscriptionPlanGrid';
import { CreditPackGrid, type CreditPackRow } from '../billing/CreditPackGrid';
import { startCreditPackCheckout, startSubscriptionCheckout } from '../../utils/billingCheckout';

export const InsufficientCreditsModal = () => {
  const { closeModal, session, requiredCredits, isAuthenticated } = useAppStore();
  const [showDevPreview, setShowDevPreview] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlanRow[]>([]);
  const [hasPaid, setHasPaid] = useState(false);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [creditPacks, setCreditPacks] = useState<CreditPackRow[]>([]);
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);
  const [busyPackId, setBusyPackId] = useState<string | null>(null);
  const [subErr, setSubErr] = useState<string | null>(null);
  const [packErr, setPackErr] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const isDevelopmentMock = import.meta.env.MODE === 'development' && session?.original_session_id === 'mock';

  const creditsNeeded = requiredCredits || 10;

  useEffect(() => {
    let c = false;
    (async () => {
      const { data: p, error: pe } = await supabase
        .from('subscription_plans')
        .select('id, code, name, amount_paise, credits_per_period, is_paid, is_active')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (c) return;
      if (pe) {
        setLoadErr(pe.message);
        return;
      }
      setPlans((p as SubscriptionPlanRow[]) || []);

      const { data: cp } = await supabase
        .from('credit_pack_products')
        .select('id, code, name, credits, amount_paise')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (c) return;
      setCreditPacks((cp as CreditPackRow[]) || []);

      if (!isAuthenticated) {
        setHasPaid(false);
        setCurrentPlanId(null);
        return;
      }
      const { data: pr } = await supabase.rpc('get_my_billing_profile');
      if (c) return;
      if (pr && typeof pr === 'object' && 'has_paid_subscription' in (pr as object)) {
        setHasPaid(Boolean((pr as { has_paid_subscription?: boolean }).has_paid_subscription));
        const ps = (pr as { paid_subscription?: { plan_id?: string } | null }).paid_subscription;
        setCurrentPlanId(typeof ps?.plan_id === 'string' ? ps.plan_id : null);
      }
    })();
    return () => {
      c = true;
    };
  }, [isAuthenticated]);

  const handleSubscribe = async (planId: string) => {
    if (!isAuthenticated) {
      setSubErr('Please sign in to subscribe.');
      return;
    }
    setSubErr(null);
    setBusyPlanId(planId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const result = await startSubscriptionCheckout(planId, {
        name: 'Hyper subscription',
        email: user?.email ?? undefined,
      });
      if (result.planChangedWithoutCheckout) {
        const msg =
          result.scheduleChangeAt === 'cycle_end'
            ? 'Plan change scheduled for the end of this billing period.'
            : 'Your subscription plan has been updated.';
        window.alert(msg);
        const { data: pr } = await supabase.rpc('get_my_billing_profile');
        if (pr && typeof pr === 'object') {
          const ps = (pr as { paid_subscription?: { plan_id?: string } | null }).paid_subscription;
          setCurrentPlanId(typeof ps?.plan_id === 'string' ? ps.plan_id : null);
          setHasPaid(Boolean((pr as { has_paid_subscription?: boolean }).has_paid_subscription));
        }
      }
    } catch (e) {
      setSubErr(e instanceof Error ? e.message : 'Checkout failed');
    } finally {
      setBusyPlanId(null);
    }
  };

  const handleBuyPack = async (packId: string) => {
    if (!isAuthenticated) {
      setPackErr('Please sign in to purchase credits.');
      return;
    }
    setPackErr(null);
    setBusyPackId(packId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await startCreditPackCheckout(packId, { email: user?.email ?? undefined });
    } catch (e) {
      setPackErr(e instanceof Error ? e.message : 'Checkout failed');
    } finally {
      setBusyPackId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-base z-dropdown flex flex-col">
      {/* Dev Mode Eye Icon */}
      {isDevelopmentMock && (
        <div className="absolute top-8 left-8 z-toast">
          <button
            onClick={() => setShowDevPreview(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-accent/20 border border-accent/40 text-accent rounded-lg font-medium hover:bg-accent/30 transition-all"
            title="Preview Component (Dev Mode)"
          >
            <Eye className="w-4 h-4" />
            <span className="text-sm">Preview</span>
          </button>
        </div>
      )}

      {/* Close Button - Development Mode Only */}
      {isDevelopmentMock && (
        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-toast">
          <button
            onClick={closeModal}
            className="p-3 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all backdrop-blur-sm"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-8">
        <div className="mx-auto max-w-4xl space-y-8 text-center">
          <div className="relative mx-auto max-w-md">
            <div className="relative">
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-status-error shadow-2xl shadow-status-error/30">
                <Zap className="h-12 w-12 text-white" />
              </div>
              <div className="absolute inset-0 mx-auto w-24">
                <div className="h-full w-full animate-ping rounded-full border-4 border-status-error/30" />
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-md space-y-3">
            <h2 className="text-3xl font-bold text-th-primary">Insufficient Credits</h2>
            <p className="text-lg leading-relaxed text-th-secondary">
              You need more credits to continue. Choose a monthly plan (credits reset each billing period—no rollover), or go to
              your profile to manage an existing subscription and invoices.
            </p>
          </div>

          <div className="mx-auto max-w-md rounded-xl border border-status-error/30 bg-status-error/10 p-6">
            <div className="flex items-center justify-center space-x-3">
              <Zap className="h-6 w-6 text-status-warning" />
              <div className="text-center">
                <p className="font-semibold text-th-primary">{creditsNeeded} credits required for this action</p>
                <p className="text-sm text-th-secondary">
                  On the Free plan you get 100 credits per month. Upgrading adds a larger monthly credit bucket.
                </p>
              </div>
            </div>
          </div>

          {loadErr ? <p className="text-sm text-status-error">{loadErr}</p> : null}

          <p className="text-sm text-th-tertiary">
            {hasPaid
              ? 'Upgrade or switch plans anytime. Downgrades apply from the next billing cycle.'
              : 'Free: 100 credits per month, resets on the 1st of each month (UTC).'}
          </p>

          <div className="w-full text-left">
            <SubscriptionPlanGrid
              plans={plans}
              hasPaidSubscription={hasPaid}
              currentPlanId={currentPlanId}
              busyPlanId={busyPlanId}
              error={subErr}
              onSubscribe={handleSubscribe}
              isGuest={!isAuthenticated}
            />
            {creditPacks.length > 0 ? (
              <div className="mt-6">
                <CreditPackGrid
                  packs={creditPacks}
                  busyPackId={busyPackId}
                  error={packErr}
                  onBuy={handleBuyPack}
                  isGuest={!isAuthenticated}
                />
              </div>
            ) : null}
            <p className="mt-4 text-center text-sm text-th-tertiary">
              <Link to="/profile" className="text-brand hover:underline" onClick={closeModal}>
                Open billing on profile
              </Link>
            </p>
          </div>

          <div className="space-y-6">
            <div className="space-y-6">
              {/* Creative Message */}
              <div className="bg-elevated border border-brand/30 rounded-2xl p-6 text-center space-y-4">
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-8 h-8 bg-brand rounded-full flex items-center justify-center animate-pulse">
                    <span className="text-white text-sm font-bold">⚡</span>
                  </div>
                  <h3 className="text-xl font-bold text-th-primary">Keep HYPER Alive</h3>
                  <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center animate-pulse">
                    <span className="text-white text-sm font-bold">💫</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-th-primary font-medium text-lg leading-relaxed">
                    Your credits power the AI magic that brings creativity to life
                  </p>
                  <p className="text-th-secondary text-sm leading-relaxed">
                    Every credit you purchase helps us maintain powerful servers, develop new AI models, and keep HYPER running smoothly for millions of creators worldwide
                  </p>
                </div>

                <div className="flex items-center justify-center space-x-6 pt-2">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-brand/20 rounded-full flex items-center justify-center mb-2">
                      <span className="text-brand text-lg">🚀</span>
                    </div>
                    <p className="text-th-secondary text-xs">Faster AI</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mb-2">
                      <span className="text-accent text-lg">🌟</span>
                    </div>
                    <p className="text-th-secondary text-xs">New Models</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-status-warning/20 rounded-full flex items-center justify-center mb-2">
                      <span className="text-status-warning text-lg">🌍</span>
                    </div>
                    <p className="text-th-secondary text-xs">Global Access</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
            </div>
          </div>

          {/* Additional Info */}
        </div>
      </div>

      {/* Dev Mode Preview Modal */}
      {showDevPreview && isDevelopmentMock && (
        <div className="fixed inset-0 bg-base/95 z-[80] flex items-center justify-center p-4">
          <div className="bg-surface border border-subtle rounded-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-subtle">
              <div className="flex items-center space-x-3">
                <Eye className="w-6 h-6 text-accent" />
                <div>
                  <h3 className="text-lg font-semibold text-th-primary">Component Preview</h3>
                  <p className="text-sm text-th-secondary">Dev Mode - Insufficient Credits Modal</p>
                </div>
              </div>
              <button
                onClick={() => setShowDevPreview(false)}
                className="p-2 text-th-secondary hover:text-th-primary transition-colors rounded-lg hover:bg-overlay"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-accent/10 border border-accent/30 rounded-xl p-4">
                <h4 className="text-accent font-medium mb-2">🎭 Component Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-th-secondary">Component:</span>
                    <span className="text-th-primary font-mono">InsufficientCreditsModal</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-th-secondary">Trigger:</span>
                    <span className="text-th-primary">Credit deduction failure</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-th-secondary">Animation:</span>
                    <span className="text-th-primary">Animated arrow (top-right)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-th-secondary">Mode:</span>
                    <span className="text-status-warning font-medium">Development Mock</span>
                  </div>
                </div>
              </div>

              <div className="bg-status-warning/10 border border-status-warning/30 rounded-xl p-4">
                <h4 className="text-status-warning font-medium mb-2">🔧 Usage</h4>
                <ul className="text-th-secondary text-sm space-y-1 list-disc list-inside">
                  <li>Triggers automatically when user has insufficient credits</li>
                  <li>Shows clear instructions for purchasing credits</li>
                  <li>Animated arrow guides user to Hyper app interface</li>
                  <li>Only visible in production when real credit check fails</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDevPreview(false)}
                  className="flex-1 px-4 py-2 bg-accent text-surface rounded-xl font-medium hover:bg-accent/90 transition-all"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Background Effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-status-error/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-status-warning/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-3/4 left-1/2 w-80 h-80 bg-brand/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};
